import React, { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";

const RogueAPDetectionChart = ({ data = [] }) => {
  const theme = useTheme();
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const chartData = useMemo(() => {
    const endTime = dayjs().startOf("hour").subtract(1, "hour");
    const startTime = endTime.subtract(23, "hour");

    const dataMap = new Map();

    data.forEach((item) => {
      if (!item?.label) return;

      const hour = item.label.slice(0, 2);
      dataMap.set(hour, Number(item.value) || 0);
    });

    return Array.from({ length: 24 }, (_, index) => {
      const currentTime = startTime.add(index, "hour");
      const label = currentTime.format("HH:mm");
      const hour = currentTime.format("HH");

      return {
        label,
        value: dataMap.get(hour) ?? 0,
      };
    });
  }, [data]);

  const option = useMemo(() => {
    const xAxisData = chartData.map((item) => item.label);

    return {
      animation: false,
      backgroundColor: theme.palette.primary.white,
      grid: {
        top: 20,
        right: 24,
        bottom: 36,
        left: 42,
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: theme.palette.primary.blackTransparency,
        borderWidth: 0,
        textStyle: {
          color: theme.palette.primary.white,
          fontSize: theme.typography.customFontSize[0],
        },
        formatter: (params) => {
          const point = params?.[0];
          if (!point) return "";
          return `${point.axisValue}<br/>Rogue SSID: ${point.data}`;
        },
        axisPointer: {
          type: "shadow",
        },
      },
      xAxis: {
        type: "category",
        boundaryGap: true,
        data: xAxisData,
        axisLabel: {
          color: theme.palette.primary.greyFourth,
          fontSize: theme.typography.customFontSize[0],
          margin: 12,
          interval: 0,
          formatter: (value, index) => {
            const lastIndex = xAxisData.length - 1;

            if (index % 4 === 0) return value;
            if (index === lastIndex) return value;

            return "";
          },
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: theme.palette.primary.greyThread,
            width: 1,
          },
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: true,
          interval: 0,
          lineStyle: {
            color: theme.palette.primary.greyThread,
            width: 1,
            type: "dashed",
          },
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: 8,
        interval: 2,
        axisLabel: {
          color: theme.palette.primary.greyFourth,
          fontSize: theme.typography.customFontSize[0],
          margin: 12,
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: theme.palette.primary.greyThread,
            width: 1,
            type: "dashed",
          },
        },
      },
      series: [
        {
          name: "Congested Ports",
          type: "bar",
          data: chartData.map((item) => item.value),
          barMaxWidth: 16,
          itemStyle: {
            color: theme.palette.primary.main,
            borderRadius: [2, 2, 0, 0],
          },
        },
      ],
    };
  }, [chartData, theme]);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart =
      echarts.getInstanceByDom(chartRef.current) ||
      echarts.init(chartRef.current);

    chartInstanceRef.current = chart;
    chart.setOption(option, true);
    chart.resize();

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);

      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, [option]);

  return (
    <div
      ref={chartRef}
      style={{
        width: "100%",
        minWidth: 0,
        height: "260px",
      }}
    />
  );
};

export default RogueAPDetectionChart;